import { createTenantViewRequestId } from "./tenantViewIds.js";
import { requireBrowserStorageOwner } from "./browserStorageScope.js";

const PREFIX = "originDashboard.tenantViewWrite.v2";
const REQUEST_KEYS = Object.freeze([
    "definitionJson",
    "formatVersion",
    "kind",
    "name",
    "title",
]);

function part(value, label) {
    const text = String(value ?? "").trim();
    if (!text) throw new TypeError(`${label} is required for durable tenant view writes`);
    return encodeURIComponent(text);
}

function keyOf(scope) {
    const owner = requireBrowserStorageOwner(scope.tenantId);
    return [
        PREFIX,
        owner.key,
        part(scope.operation, "operation"),
        part(scope.kind, "kind"),
        part(scope.sourceKey, "sourceKey"),
    ].join(":");
}

function boundRequest(value, scope) {
    if (value === undefined) return undefined;
    const prototype = value && typeof value === "object"
        ? Object.getPrototypeOf(value)
        : null;
    if (
        !value
        || typeof value !== "object"
        || Array.isArray(value)
        || (prototype !== Object.prototype && prototype !== null)
        || Object.keys(value).sort().join("\0") !== REQUEST_KEYS.join("\0")
        || value.kind !== scope.kind
        || !["kernel", "canvas"].includes(value.kind)
        || typeof value.name !== "string"
        || !value.name
        || typeof value.title !== "string"
        || !value.title
        || !Number.isSafeInteger(value.formatVersion)
        || value.formatVersion < 0
        || typeof value.definitionJson !== "string"
        || !value.definitionJson
    ) {
        throw new TypeError("durable tenant view request binding is malformed");
    }
    return {
        kind: value.kind,
        name: value.name,
        title: value.title,
        formatVersion: value.formatVersion,
        definitionJson: value.definitionJson,
    };
}

function parseEntry(raw, scope) {
    if (raw === null) return null;
    let value;
    try {
        value = JSON.parse(raw);
    } catch {
        throw new TypeError("durable tenant view write state is malformed");
    }
    if (
        !value
        || typeof value !== "object"
        || value.operation !== scope.operation
        || value.kind !== scope.kind
        || value.sourceKey !== scope.sourceKey
        || !["pending", "complete"].includes(value.state)
        || typeof value.createRequestId !== "string"
        || !value.createRequestId
    ) {
        throw new TypeError("durable tenant view write state is malformed");
    }
    if (
        value.state === "complete"
        && (
            typeof value.tenantViewId !== "string"
            || !value.tenantViewId
            || !["string", "number"].includes(typeof value.version)
        )
    ) {
        throw new TypeError("completed tenant view write state is malformed");
    }
    const request = boundRequest(value.request, scope);
    return request === undefined ? value : { ...value, request };
}

function requestsMatch(left, right) {
    return REQUEST_KEYS.every((key) => left?.[key] === right?.[key]);
}

export function openTenantViewWriteJournal(
    scope,
    {
        storage = globalThis.localStorage,
        cryptoImpl = globalThis.crypto,
    } = {},
) {
    if (!storage?.getItem || !storage?.setItem || !storage?.removeItem) {
        throw new TypeError("durable browser storage is unavailable");
    }
    const key = keyOf(scope);
    const read = () => parseEntry(storage.getItem(key), scope);
    return Object.freeze({
        read,
        begin(request) {
            const normalizedRequest = boundRequest(request, scope);
            const existing = read();
            if (existing) {
                if (
                    normalizedRequest !== undefined
                    && (
                        existing.request === undefined
                        || !requestsMatch(existing.request, normalizedRequest)
                    )
                ) {
                    throw new Error(
                        "durable tenant view request conflicts with the existing write",
                    );
                }
                return existing;
            }
            const entry = {
                state: "pending",
                operation: scope.operation,
                kind: scope.kind,
                sourceKey: scope.sourceKey,
                createRequestId: createTenantViewRequestId(cryptoImpl),
                createdAt: Date.now(),
                ...(normalizedRequest === undefined ? {} : { request: normalizedRequest }),
            };
            // This durable write must happen before the network request.
            storage.setItem(key, JSON.stringify(entry));
            return entry;
        },
        complete(pending, tenantViewId, version) {
            const entry = {
                ...pending,
                state: "complete",
                tenantViewId,
                version,
            };
            storage.setItem(key, JSON.stringify(entry));
            return entry;
        },
        clear() {
            storage.removeItem(key);
        },
    });
}
